import type { PendingConversation } from "@rakazo/chat-ui/workspace-result";
import { PendingConversationDraftSchema } from "@rakazo/chat-ui/workspace-result";
import { ArtifactSourceReferenceSchema, PendingTicketDraftSchema } from "@rakazo/contracts";
import type { SetStateAction } from "react";
import { useContext, useEffect, useRef, useState } from "react";
import type { PendingAttachment, PendingTicketReference } from "../pages/shell/chat-shared";
import { WorkspaceScopeContext } from "./workspace-local-state";

type Record = {
  revision: string;
  items: PendingAttachment[];
  conversations?: PendingConversation[];
  tickets?: PendingTicketReference[];
};
type StoredRecord = {
  revision: string;
  conversations?: PendingConversation[];
  tickets?: PendingTicketReference[];
  items: Array<{
    id: string;
    threadKey: string;
    source?: PendingAttachment["source"];
    file: { name: string; type: string; lastModified: number; bytes: ArrayBuffer };
  }>;
};
const MAX_BYTES = 50 * 1024 * 1024;
export async function encodeAttachmentRecord(record: Record): Promise<StoredRecord> {
  return {
    revision: record.revision,
    ...(record.conversations
      ? { conversations: PendingConversationDraftSchema.parse(record.conversations) }
      : {}),
    ...(record.tickets?.length ? { tickets: PendingTicketDraftSchema.parse(record.tickets) } : {}),
    items: await Promise.all(
      record.items.map(async ({ id, threadKey, file, source }) => ({
        id,
        threadKey,
        ...(source ? { source } : {}),
        file: {
          name: file.name,
          type: file.type,
          lastModified: file.lastModified,
          bytes: await file.arrayBuffer(),
        },
      })),
    ),
  };
}
export function decodeAttachmentRecord(record: Record | StoredRecord): Record {
  if (typeof record.revision !== "string" || !Array.isArray(record.items))
    throw new Error("Invalid attachment draft");
  const items = record.items.map(({ id, threadKey, file, source: storedSource }) => {
    const source = storedSource ? ArtifactSourceReferenceSchema.parse(storedSource) : undefined;
    if (typeof id !== "string" || typeof threadKey !== "string")
      throw new Error("Invalid attachment draft");
    // Keep recovery copies written by earlier builds readable.
    if (file instanceof File) return { id, threadKey, file, ...(source ? { source } : {}) };
    if (
      !file ||
      !(file.bytes instanceof ArrayBuffer) ||
      typeof file.name !== "string" ||
      typeof file.type !== "string" ||
      !Number.isFinite(file.lastModified)
    )
      throw new Error("Invalid attachment draft");
    return {
      id,
      threadKey,
      ...(source ? { source } : {}),
      file: new File([file.bytes], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      }),
    };
  });
  if (items.reduce((sum, item) => sum + item.file.size, 0) > MAX_BYTES)
    throw new Error("Invalid attachment draft");
  return {
    revision: record.revision,
    ...(record.conversations
      ? { conversations: PendingConversationDraftSchema.parse(record.conversations) }
      : {}),
    items,
    ...(record.tickets ? { tickets: PendingTicketDraftSchema.parse(record.tickets) } : {}),
  };
}
async function access(key: string, next?: Record, revision?: string): Promise<Record | null> {
  // WebKit can reject File/Blob values in IndexedDB. Read bytes before opening
  // the transaction so asynchronous conversion cannot make it inactive.
  const storedNext = next ? await encodeAttachmentRecord(next) : undefined;
  return new Promise((resolve, reject) => {
    const open = indexedDB.open("andrew-workspace-drafts", 1);
    open.onupgradeneeded = () => open.result.createObjectStore("attachments");
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result;
      const transaction = db.transaction("attachments", next ? "readwrite" : "readonly");
      const store = transaction.objectStore("attachments");
      const request = store.get(key);
      let value: Record | StoredRecord | null = null;
      request.onsuccess = () => {
        value = request.result ?? null;
        if (!storedNext) return;
        if ((value?.revision ?? "") !== revision) {
          transaction.abort();
          return;
        }
        store.put(storedNext, key);
        value = storedNext;
      };
      transaction.oncomplete = () => {
        db.close();
        try {
          resolve(value ? decodeAttachmentRecord(value) : null);
        } catch (error) {
          reject(error);
        }
      };
      transaction.onabort = transaction.onerror = () => {
        db.close();
        reject(new Error("Attachment recovery unavailable"));
      };
    };
  });
}

/** Files remain on this device until Send; writes are serialized and reject stale views. */
export function useAttachmentDraft() {
  const scope = useContext(WorkspaceScopeContext);
  const [items, setItems] = useState<PendingAttachment[]>([]);
  const current = useRef(items);
  const [conversations, setConversations] = useState<PendingConversation[]>([]);
  const currentConversations = useRef(conversations);
  const [tickets, setTickets] = useState<PendingTicketReference[]>([]);
  const currentTickets = useRef(tickets);
  const [ready, setReady] = useState(false);
  const [durable, setDurable] = useState(true);
  const [failed, setFailed] = useState(false);
  const revision = useRef("");
  const initialized = useRef(false);
  const writes = useRef(Promise.resolve());
  useEffect(() => {
    if (initialized.current) return;
    let live = true;
    void (scope ? access(scope) : Promise.resolve(null))
      .then((saved) => {
        if (!live) return;
        revision.current = saved?.revision ?? "";
        current.current = saved?.items ?? [];
        setItems(current.current);
        currentTickets.current = saved?.tickets ?? [];
        setTickets(currentTickets.current);
        currentConversations.current = saved?.conversations ?? [];
        setConversations(currentConversations.current);
      })
      .catch(() => {
        if (live) {
          setDurable(false);
          setFailed(true);
        }
      })
      .finally(() => {
        if (live) {
          initialized.current = true;
          setReady(true);
        }
      });
    return () => {
      live = false;
    };
  }, [scope]);
  function update(change: SetStateAction<PendingAttachment[]>) {
    const next = typeof change === "function" ? change(current.current) : change;
    current.current = next;
    setItems(next);
    persist(next, currentTickets.current);
  }
  function updateTickets(change: SetStateAction<PendingTicketReference[]>) {
    const next = typeof change === "function" ? change(currentTickets.current) : change;
    currentTickets.current = next;
    setTickets(next);
    persist(current.current, next);
  }
  function updateConversations(change: SetStateAction<PendingConversation[]>) {
    if (!initialized.current) throw new Error("Draft recovery pending");
    const next = PendingConversationDraftSchema.parse(
      typeof change === "function" ? change(currentConversations.current) : change,
    );
    currentConversations.current = next;
    setConversations(next);
    persist(current.current, currentTickets.current);
  }
  function persist(next: PendingAttachment[], nextTickets: PendingTicketReference[]) {
    const nextConversations = currentConversations.current;
    setDurable(false);
    setFailed(false);
    writes.current = writes.current
      .catch(() => {})
      .then(async () => {
        if (
          !scope ||
          !initialized.current ||
          next.reduce((sum, item) => sum + item.file.size, 0) > MAX_BYTES
        ) {
          setFailed(true);
          return;
        }
        try {
          const saved = await access(
            scope,
            {
              revision: crypto.randomUUID(),
              items: next,
              tickets: nextTickets,
              conversations: nextConversations,
            },
            revision.current,
          );
          revision.current = saved!.revision;
          if (
            current.current === next &&
            currentTickets.current === nextTickets &&
            currentConversations.current === nextConversations
          )
            setDurable(true);
        } catch {
          setFailed(true);
        }
      });
  }
  return {
    conversations,
    updateConversations,
    items,
    update,
    tickets,
    updateTickets,
    ready,
    durable,
    failed,
  };
}
